import { useState, useEffect } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { ScoreHistoryOverview } from '@/components/score/ScoreHistoryOverview'
import { ChartDataPoint } from '@/types'
import { Download, Calendar, Filter, TrendingUp, TrendingDown } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { useToast } from '@/components/ui/MobileToast'
import { businessService } from '@/services/businessService'

export function ScoreHistoryPage() {
  const { user } = useAuth()
  const [showFilters, setShowFilters] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [historyData, setHistoryData] = useState<ChartDataPoint[]>([])
  const [businessId, setBusinessId] = useState<string>('')
  const showToast = useToast()

  useEffect(() => {
    loadScoreHistory()
  }, [])

  const loadScoreHistory = async () => {
    setIsLoading(true)
    try {
      // If user has a businessId, fetch that specific business
      if (user?.businessId) {
        try {
          setBusinessId(user.businessId)
          
          // Get score history
          const historyResponse = await businessService.getScoreHistory(user.businessId)
          
          // Convert to chart format
          const chartData: ChartDataPoint[] = (historyResponse.history || []).map((record: any) => ({
            date: new Date(record.timestamp).toISOString().split('T')[0],
            score: parseFloat(record.score),
            band: record.band
          }))
          
          setHistoryData(chartData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()))
          return
        } catch (error) {
          console.error('Failed to load user business history:', error)
        }
      }
      
      // Fallback: Get first business for demo (for users without businessId)
      const response = await businessService.getBusinesses({ limit: 1 })
      if (response.businesses.length > 0) {
        const business = response.businesses[0]
        setBusinessId(business.id)
        
        // Get score history
        const historyResponse = await businessService.getScoreHistory(business.id)
        
        // Convert to chart format
        const chartData: ChartDataPoint[] = (historyResponse.history || []).map((record: any) => ({
          date: new Date(record.timestamp).toISOString().split('T')[0],
          score: parseFloat(record.score),
          band: record.band
        }))
        
        setHistoryData(chartData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()))
      }
    } catch (error) {
      console.error('Failed to load score history:', error)
      showToast.showToast({
        type: 'error',
        title: 'Load Failed',
        message: 'Unable to load score history.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleExportData = () => {
    showToast.showToast({
      type: 'info',
      title: 'Export Started',
      message: 'Exporting score history data...'
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading score history...</span>
      </div>
    )
  }

  // Calculate insights
  const scores = historyData.map(d => d.score)
  const oldestScore = scores[0] || 0
  const newestScore = scores[scores.length - 1] || 0
  const scoreChange = newestScore - oldestScore
  const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length
  const maxScore = Math.max(...scores)
  const minScore = Math.min(...scores)

  // Find band transitions
  const bandTransitions = historyData.filter((d, i) => 
    i > 0 && d.band !== historyData[i - 1].band
  ).slice(-3).reverse()

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Score History
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track your TLBH score evolution and identify trends over time ({historyData.length} records)
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <Filter className="w-4 h-4" />
            Filters
          </button>
          
          <button
            onClick={handleExportData}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
            Filter Options
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Date Range
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Score Range
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Min"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  placeholder="Max"
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Band Filter
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent">
                <option value="">All Bands</option>
                <option value="A+">A+ (90-100)</option>
                <option value="A">A (80-89)</option>
                <option value="B+">B+ (70-79)</option>
                <option value="B">B (60-69)</option>
                <option value="C+">C+ (50-59)</option>
                <option value="C">C (40-49)</option>
                <option value="D">D (0-39)</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-3 mt-4">
            <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
              Apply Filters
            </button>
            <button className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              Reset
            </button>
          </div>
        </div>
      )}

      {/* Score History Chart */}
      {historyData.length > 0 ? (
        <ScoreHistoryOverview data={historyData} />
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No History Available
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Score history will appear here once data is available
          </p>
        </div>
      )}

      {/* Key Insights */}
      {historyData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Key Insights
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${scoreChange >= 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Overall Trend
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Your score has {scoreChange >= 0 ? 'improved' : 'decreased'} by {Math.abs(scoreChange).toFixed(2)} points
                    ({scoreChange >= 0 ? '+' : ''}{((scoreChange / oldestScore) * 100).toFixed(1)}%)
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Average Score
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Your average score over this period is {avgScore.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    Score Range
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Ranges from {minScore.toFixed(2)} to {maxScore.toFixed(2)} (volatility: {(maxScore - minScore).toFixed(2)})
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Recent Band Changes
            </h3>
            {bandTransitions.length > 0 ? (
              <div className="space-y-3">
                {bandTransitions.map((transition, index) => {
                  const prevBand = historyData[historyData.indexOf(transition) - 1]?.band
                  const isImprovement = transition.band > prevBand
                  return (
                    <div key={index} className={`flex items-center justify-between p-3 rounded-lg ${
                      isImprovement 
                        ? 'bg-green-50 dark:bg-green-900/20' 
                        : 'bg-red-50 dark:bg-red-900/20'
                    }`}>
                      <div>
                        <p className={`text-sm font-medium ${
                          isImprovement 
                            ? 'text-green-800 dark:text-green-200' 
                            : 'text-red-800 dark:text-red-200'
                        }`}>
                          {prevBand} → {transition.band}
                        </p>
                        <p className={`text-xs ${
                          isImprovement 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {new Date(transition.date).toLocaleDateString('en-IN', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className={`text-lg font-bold ${
                        isImprovement 
                          ? 'text-green-800 dark:text-green-200' 
                          : 'text-red-800 dark:text-red-200'
                      }`}>
                        {transition.score.toFixed(2)}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No band changes recorded in recent history
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}